import { useMutation, useQuery, useQueryClient } from "react-query";
import servicesUtils from "../../../utils/servicesUtils";
import type { JiraAdfNode } from "../jiraRichText";

interface JiraComment {
  id: string;
  author: {
    displayName: string;
    avatarUrls: {
      "48x48": string;
    };
  };
  /** Plain string or Atlassian Document Format (mentions, paragraphs, etc.) */
  body: string | JiraAdfNode;
  created: string;
  updated?: string;
  /** JSM only: false = internal (not on portal), true = customer-visible */
  jsdPublic?: boolean;
}

interface JiraCommentsResponse {
  comments: JiraComment[];
  total: number;
}

interface AddCommentRequest {
  body: string;
}

const COMMENTS_POLL_INTERVAL_MS = 15 * 1000; // 15 seconds

const useJiraComments = (ticketKey: string, enabled: boolean = true) => {
  return useQuery<JiraCommentsResponse, Error>(
    ["jiraComments", ticketKey],
    () => {
      return servicesUtils.getService<JiraCommentsResponse>("/jira_ticket/comments/", {
        ticket_key: ticketKey,
      });
    },
    {
      enabled: enabled && !!ticketKey,
      staleTime: 0, // Always consider stale so refetch on window focus and after add-comment
      retry: 1,
      // Poll for new comments so Jira replies appear without sending a comment (only when tab is focused)
      refetchInterval: enabled && ticketKey ? COMMENTS_POLL_INTERVAL_MS : false,
    }
  );
};

export const useAddJiraComment = (ticketKey: string) => {
  const queryClient = useQueryClient();

  return useMutation<JiraComment, Error, AddCommentRequest>(
    (data: AddCommentRequest) => {
      // Pass ticket_key as query parameter in URL
      const url = `/jira_ticket/comments/?ticket_key=${ticketKey}`;
      return servicesUtils.postService<JiraComment>(url, data);
    },
    {
      onSuccess: () => {
        // Invalidate and refetch comments after adding a new one
        queryClient.invalidateQueries(["jiraComments", ticketKey]);
      },
    }
  );
};

export default useJiraComments;
export type { JiraComment, JiraCommentsResponse };
